<script setup lang="ts">
import { Dayjs } from 'dayjs'
import type {
  NovaCalendarEmits,
  NovaCalendarProps,
  CalendarSlotType,
} from './NovaCalendar.types'

const emit = defineEmits<NovaCalendarEmits>()
const props = withDefaults(defineProps<NovaCalendarProps>(), {
  fillParent: false,
  checkedInDates: () => [],
  loading: false,
})

const { locale } = useI18n()
const { dayjs } = useDayjs()
const year = ref<number>()
const month = ref<number>()
const monthNames = ref<string[]>([])
const weekdays = ref<string[]>([])
const initDayModel = ref<ReturnType<typeof dayjs>>()
// const today = ref<ReturnType<typeof dayjs>>(dayjs())
const daysInMonth = ref<number>()
const calendarSlots = ref<CalendarSlotType[]>([])

watch(
  () => locale.value,
  () => {
    const instanceLocaleData = dayjs().localeData()
    monthNames.value = instanceLocaleData.monthsShort()
    weekdays.value = instanceLocaleData.weekdaysShort()
  },
  { immediate: true }
)

watch(
  () => [initDayModel.value, props.checkedInDates],
  ([curDay]) => {
    if (curDay) {
      const _curDay = curDay as Dayjs
      setDaysOfMonth(_curDay)
    }
  },
  { immediate: true }
)

onBeforeMount(() => {
  onChangeInitDay(props.initDay ? props.initDay : dayjs())
})

const onChangeInitDay = (day: Dayjs) => {
  initDayModel.value = day
}

const setDaysOfMonth = (day: ReturnType<typeof dayjs>) => {
  const _year = day.year()
  const _month = day.month()

  year.value = _year
  month.value = _month
  daysInMonth.value = day.daysInMonth()

  createCalendarSlots(_year, _month, dayjs().format('YYYY-MM-DD'))
}

const getIsCheckedInDate = (date: string) =>
  props.checkedInDates.findIndex((item) => item === date) !== -1

const createCalendarSlots = (year: number, month: number, today: string) => {
  const startDate = dayjs(`${year}-${month + 1}-01`)
  const endDate = startDate.add(1, 'month').add(-1, 'day')
  const startDayOfWeek = startDate.day()
  const endDayOfMonth = endDate.date()
  const previousMonthEndDate = startDate.subtract(1, 'day')

  const _calendarSlots: CalendarSlotType[] = Array.from(
    { length: startDayOfWeek + endDayOfMonth > 35 ? 7 * 6 : 7 * 5 },
    (_, index) => {
      // 남은 이전 달의 날
      if (index < startDayOfWeek) {
        const _date = previousMonthEndDate.subtract(
          startDayOfWeek - index - 1,
          'day'
        )
        const date = _date.format('YYYY-MM-DD')
        const day = _date.format('D')

        return {
          slot: index + 1,
          date,
          day,
          type: 'PREV',
          isCheckedIn: getIsCheckedInDate(date),
          isToday: date === today,
        }
      }

      // 다음 달의 날
      if (index >= startDayOfWeek + endDayOfMonth) {
        const _date = endDate.add(
          index - startDayOfWeek - endDayOfMonth + 1,
          'day'
        )
        const date = _date.format('YYYY-MM-DD')
        const day = _date.format('D')

        return {
          slot: index + 1,
          date,
          day,
          type: 'NEXT',
          isCheckedIn: getIsCheckedInDate(date),
          isToday: date === today,
        }
      }

      // 현재 달의 날
      const _date = startDate.add(index - startDayOfWeek, 'day')
      const date = _date.format('YYYY-MM-DD')
      const day = _date.format('D')

      return {
        slot: index + 1,
        date,
        day,
        type: 'CURRENT',
        isCheckedIn: getIsCheckedInDate(date),
        isToday: date === today,
      }
    }
  )

  calendarSlots.value = _calendarSlots
}

const handleOnUpdateDay = (day: ReturnType<typeof dayjs>) => {
  onChangeInitDay(day)
  emit('update:day', day)
}
</script>

<template>
  <ClientOnly>
    <NovaBoxBase :class="['calendar', { 'fill-parent': fillParent, loading }]">
      <NovaCalendarControl
        :day="initDayModel"
        @update:day="handleOnUpdateDay"
      />
      <NovaCalendarSlots :calendar-slots="calendarSlots" :weekdays="weekdays" />

      <NovaLoadingIndicator
        v-if="loading"
        :overlay="true"
        :bg-bright="'light'"
      />
    </NovaBoxBase>
  </ClientOnly>
</template>

<style lang="scss" scoped>
.calendar {
  position: relative;
  display: flex;
  flex-direction: column;
  width: auto;
  height: auto;
  padding: 0;

  &.fill-parent {
    height: 100%;
    width: 100%;
    border-radius: 0;
  }

  &.loading {
    pointer-events: none;
  }
}
</style>
